import { useCallback, useEffect, useRef, useState } from 'react'
import { ButtonPrimary } from '../../../../../../baseComponents/Button'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { InputNumberWithLabelMargin, InputWithLabelMarginRemove } from '../../../../../../forms/Input'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import { ProductExtendedTyShort } from '../../../../../Product/types'
import { ModalOpenedSetTy } from '../../../../../../baseComponents/Modal/types'
import { TextField } from '../../../../../../forms/TextField'
import { LittleModalCreateEdit } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { BeforeSubmitHandler } from '../../../../../../forms/TwinForm/types'
import { changeErrorMessage } from '../../../../../../utils/reducers/reduxDispatch'
import { productTypesInversed } from '@teinor/erp/types/company/product'
import { OrderUniqueNumber } from '@teinor/erp/types/company/order/general'
import { InputTableURLsTable } from '../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ModalComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { useDebounce } from 'use-debounce'
import LoadingSpinner from '../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import SearchBar from '../../../../../../baseComponents/SearchBar'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import useOnClickOutside from '../../../../../../utils/hooks/useOnClickOut'
import { SearchBarProductResultUniqueNumber } from '../../../../../Product/SearchBarProduct/SearchBarProductResults'
import { OrderSearchProductInputChangeSearchFunc, OnChangeOrderAddProduct } from '../../../OrderLayout/OrderTable/TableComponents/Inputs/OrderSearchProductInput/types'
import { ITLayoutTableModalOpenSetTy } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'

interface ModalUniqueNumberProductOrderSellProps extends ModalUniqueNumberProductOrderSellLogicProps { }

const ModalUniqueNumberProductOrderSell: React.FC<ModalUniqueNumberProductOrderSellProps> = ({ setOpened, onSubmit, allRowData, inputTableURLs }) => {
    const { t } = useTwinTranslation()
    const isSerialized = allRowData?.['productType'] === productTypesInversed['serialized']
    const { checkAllUniqueNumberOk, products, updateUniqueNumber, updateUniqueNumberQuantity, addInputSerial, removeUniqueNumber } = useModalUniqueNumberProductOrderSellLogic({ setOpened, onSubmit, allRowData, inputTableURLs, isSerialized })
    const readOnly = allRowData?.readOnly
    const title = isSerialized ? t('serialNumber', 'Número de serie') : t('configLotNumber', 'Configurador Número de lote')
    const renderThis: JSX.Element[] = []
    for (const key in products) {
        const index = parseInt(key)
        renderThis.push(<InputUniqueNumberLot key={index} {...{ index, readOnly, allRowData: allRowData || {}, products, updateUniqueNumber, updateUniqueNumberQuantity, setOpened, removeUniqueNumber }} isSerialized={isSerialized} />)
    }
    return (
        <LittleModalCreateEdit url={inputTableURLs.updateData} setOpened={setOpened} onSubmit={onSubmit} translations={{
            title,
            button: t('save', 'Guardar')
        }} haveButtonPermissions={readOnly ? false : true} beforeSubmit={checkAllUniqueNumberOk} fields={[]}>
            {renderThis}
            {!readOnly && !isSerialized ? <ButtonUniqueNumber {...{ addInputSerial }} /> : null}
        </LittleModalCreateEdit>
    )
}
export interface OrderUniqueNumberExtended extends OrderUniqueNumber {
    name: string
}
interface ModalUniqueNumberProductOrderSellLogicProps extends ModalComponentProps { 
    isSerialized: boolean
    onSubmit: () => void
    inputTableURLs: InputTableURLsTable
    allRowData?: RowData
}
const useModalUniqueNumberProductOrderSellLogic = ({ allRowData, isSerialized }: ModalUniqueNumberProductOrderSellLogicProps) => {
    const { t } = useTwinTranslation()
    const parsedUniqueNumber = allRowData?.unique_number || []
    const [products, setProducts] = useState<OrderUniqueNumberExtended[]>(() => {
        if (isSerialized) {
            const arr = parsedUniqueNumber.length > allRowData?.quantity ? [] : Array(parseInt(allRowData?.quantity) - parsedUniqueNumber.length).fill({ quantity: 1, serialCode: '', WarehouseId: allRowData?.WarehouseId, name: '' })
            return [...parsedUniqueNumber, ...arr]
        }
        return parsedUniqueNumber?.length ? [...parsedUniqueNumber] : [{ serialCode: '', WarehouseId: allRowData?.WarehouseId, quantity: parseFloat(allRowData?.quantity), name:''}]
    })
    const errorMessageQuantity = t('notMatchQuantity', 'Las cantidades no coinciden')
    const errorMessageUnique = t('twoUniqueNumbersDuplicated', 'Dos números de serie coinciden')
    
    const updateUniqueNumber = useCallback(async (element: ProductExtendedTyShort, index: number) => {
            await setProducts((old) => {
                const oldCopy = JSON.parse(JSON.stringify(old))
                oldCopy[index] = { ...oldCopy[index], 'serialCode': element.unique_number, UniqueProductId: element.id, name: element?.['ProductLangs.name'], WarehouseId: element?.['StockParents.WarehouseId'] }
                return [...oldCopy]
            })
    }, [setProducts])

    const updateUniqueNumberQuantity = useCallback(async (myQuantity: number, index: number) => {
        setProducts((old) => {
            const oldCopy = JSON.parse(JSON.stringify(old))
            oldCopy[index]['quantity'] = myQuantity
            return [...oldCopy]
        })
    }, [setProducts])

    const addInputSerial = useCallback((e: React.FormEvent) => {
        e.stopPropagation()
        e.preventDefault()
        setProducts((old) => {
            const oldCopy = JSON.parse(JSON.stringify(old))
            let totalQuantity = 0
            for (const key in oldCopy) {
                const element = oldCopy[key]
                const quantityUniqueNumber = element.quantity
                totalQuantity += quantityUniqueNumber
            }
            const myQuantity = parseFloat(allRowData?.quantity) - totalQuantity
            oldCopy.push({ quantity: myQuantity > 0 ? myQuantity : 0 })
            return [...oldCopy]
        })
    }, [setProducts, allRowData?.quantity])

    const checkAllUniqueNumberOk: BeforeSubmitHandler = useCallback((values) => {
        const myUniqueValuesForUpdate = []
        let totalQuantity = 0
        const match: { [serialCode: string]: number[] } = {}
        const err = []
        for (const key in products) {
            const unique_number = products[key]
            if (unique_number.serialCode) {
                myUniqueValuesForUpdate.push(unique_number)
                const serialCode = unique_number.serialCode
                totalQuantity += parseFloat(String(unique_number.quantity))
                const keyParsed = parseInt(key)
                if (!match[serialCode]) {
                    match[serialCode] = []
                } else {
                    err.push(keyParsed)
                }
                match[serialCode].push(keyParsed)
            }
        }

        if (totalQuantity !== parseFloat(allRowData?.quantity)) {
            changeErrorMessage(errorMessageQuantity)
            return false
        }
        if (err.length) {
            changeErrorMessage(errorMessageUnique)
            return false
        }
        values['id'] = allRowData?.id
        values['unique_number'] = myUniqueValuesForUpdate 
        return true
    }, [allRowData, products, errorMessageQuantity, errorMessageUnique])

    const removeUniqueNumber = useCallback((index: number) => {
        setProducts((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[index] = { quantity: copyOld[index].quantity}
            return copyOld
        })
    }, [setProducts])

    return { checkAllUniqueNumberOk, products, updateUniqueNumber, addInputSerial, updateUniqueNumberQuantity, removeUniqueNumber }
}

interface InputUniqueNumberLotProps {
    index: number
    updateUniqueNumber: (element: ProductExtendedTyShort, index: number) => Promise<void>
    updateUniqueNumberQuantity: (quantity: number, index: number) => Promise<void>
    removeUniqueNumber: (index: number) => void
    products?: OrderUniqueNumberExtended[]
    allRowData: RowData
    setOpened: ModalOpenedSetTy
    readOnly?: boolean
    isSerialized: boolean
}

const InputUniqueNumberLot: React.FC<InputUniqueNumberLotProps> = ({ index, products, allRowData, setOpened, updateUniqueNumber, updateUniqueNumberQuantity, removeUniqueNumber, readOnly, isSerialized }) => {
    const { t } = useTwinTranslation()
    if (readOnly) {
        return <div className='my-10 w-full flex gap-40'><TextField label={t('quantity', 'Cantidad')} value={String(products?.[index]?.quantity || 1)} />
            <TextField label={isSerialized ? t('serialNumber', 'Número de serie') : t('lotNumber', 'Número de lote')} value={products?.[index]?.serialCode + ' - ' + products?.[index]?.name} />
        </div>
    }

    return (
        <div className='flex justify-start items-center w-full mb-14'>
            {!isSerialized ? <InputNumberWithLabelMargin className='w-55' label={t('quantity', 'Cantidad')} value={products?.[index]?.quantity || 0} onChange={(value) => updateUniqueNumberQuantity(parseInt(value), index)}/> : null}
            <div className={'w-full  text-left '+ (!isSerialized ? 'ml-40' : '')}>
                <OrderSearchProductInputUniqueNumber allRowData={allRowData || {}} onChange={(value) => updateUniqueNumber(value || '', index)} setOpenModal={() => setOpened(null)} products={products} removeUniqueNumber={removeUniqueNumber} index={index} isSerialized={isSerialized} />
            </div>
        </div>
    )
}

interface ButtonUniqueNumberProps {
    addInputSerial: (e: React.FormEvent) => void
}

const ButtonUniqueNumber: React.FC<ButtonUniqueNumberProps> = ({ addInputSerial }) => {
    return (
        <div className='flex justify-center w-full my-20'>
            <ButtonPrimary onClick={addInputSerial}>
                <TwinTrans transKey={'add'}>Añadir</TwinTrans>
            </ButtonPrimary>
        </div>
    )
}

interface OrderSearchProductInputUniqueNumberProps extends OrderSearchProductInputUniqueNumberLogicProps {
    removeUniqueNumber: (index: number) => void
    index: number
    isSerialized: boolean
}
const OrderSearchProductInputUniqueNumber: React.FC<OrderSearchProductInputUniqueNumberProps> = ({ allRowData, isSerialized, setOpenModal, onChange, products, removeUniqueNumber, index }) => {
    const { t } = useTwinTranslation()
    const { searchValue, changeSearchValue, listProducts, onSelectProduct, onFocus, ref, handleOnKeyDown, loading } = useOrderSearchProductInputUniqueNumberLogic({ onChange, allRowData, setOpenModal, products })
    const myLabel = isSerialized ? t('serialNumber', 'Número de serie') : t('lotNumber', 'Número de lote')
    if (products?.length && products?.[index]?.serialCode && Object.keys(products?.[index])?.length) {
        return <InputWithLabelMarginRemove label={myLabel} value={products[index].serialCode + ' - ' + products[index]?.name} removeFunct={() => { removeUniqueNumber(index); changeSearchValue('') }} />
    }
    if (loading) {
        return <div className='w-full'><LoadingSpinner className='w-20 h-20 mx-auto' /></div>
    }
    return (
        <div className='w-full relative input_table_searchproductinput mt-16' ref={ref}>
            <SearchBar onChange={changeSearchValue} {...{ searchValue, placeholder: myLabel, onFocus, onKeyDown: handleOnKeyDown }} />
            <SearchBarProductResultUniqueNumber products={listProducts} {...{ onSelectProduct }} />
        </div>
    )
}

interface OrderSearchProductInputUniqueNumberLogicProps {
    products?: OrderUniqueNumberExtended[]
    allRowData: RowData
    onChange: OnChangeOrderAddProduct
    setOpenModal: ITLayoutTableModalOpenSetTy
}

const useOrderSearchProductInputUniqueNumberLogic = ({ onChange, allRowData, setOpenModal, products }: OrderSearchProductInputUniqueNumberLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const [listProducts, setListProducts] = useState<ProductExtendedTyShort[] | null>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [debounceValue] = useDebounce(searchValue, 200)
    const ref = useRef<any>(null)

    const changeSearchValue: OrderSearchProductInputChangeSearchFunc = useCallback((myvalue) => {
        setSearchValue(myvalue)
    }, [setSearchValue])

    const getAllProducts = useCallback(async (mySearchValue: string) => {
        const extraWhere: TwinDictionary = {}
        if (mySearchValue) {
            const myiLike = {
                $iLike: '%' + mySearchValue + '%',
            }
            extraWhere['unique_number'] = myiLike
        } else {
            extraWhere['unique_number'] = { '$not': null }
        }
        if (products?.length) {
            const selectedProductIds = []
            for (const element of products || []) {
                if (element?.UniqueProductId) {
                    selectedProductIds.push(element.UniqueProductId)
                }
            }
            extraWhere['Product.id'] = { '$not': selectedProductIds }
        }

        extraWhere['Product.ProductId'] = allRowData?.ProductId
        if (allRowData?.Warehouses && Object.keys(allRowData?.Warehouses).length) {
            if (allRowData?.WarehouseId && parseInt(allRowData?.WarehouseId)) {
                extraWhere["StockParents.WarehouseId"] = allRowData?.WarehouseId
            } else {
                extraWhere["StockParents.WarehouseId"] = { '$or': Object.keys(allRowData?.Warehouses) }
            }
        }
        const result = await twinFetchPostJSON('/api/app/product/getAllProductsWithUniqueNumberAndStock', {
            where: {
                productType: { '$not': [5] },
                ...extraWhere
            },
            limit: 15
        })
        if (result) {
            setListProducts(result)
        } else {
            setListProducts([])
        }
    }, [allRowData?.ProductId, products, allRowData?.Warehouses, allRowData?.WarehouseId])

    const onFocus = useCallback(async () => {
        await getAllProducts('')
    }, [getAllProducts])

    const onBlur = useCallback(() => {
        setSearchValue('')
        setListProducts(null)
    }, [])

    const onSelectProduct: OnChangeOrderAddProduct = useCallback((product) => {
        startLoading()
        if (product.isVariantProduct) {
            setOpenModal({ type: 'complexProduct', allRowData: { ...product, ProductId: product.id } })
            onBlur()
            endLoading()
        } else {
            onChange({ ...product })
            endLoading()
        }
    }, [onChange, setOpenModal, onBlur, startLoading, endLoading])

    const handleOnKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (listProducts && listProducts.length) {
                onSelectProduct(listProducts[0])
            }
        }
    }, [onSelectProduct, listProducts])

    useOnClickOutside(ref, onBlur)

    useEffect(() => {
        if (debounceValue !== '') {
            getAllProducts(debounceValue)
        } else if (debounceValue === '') {
            setListProducts((old) => {
                if (old) {
                    return []
                }
                return null
            })
        }
    }, [debounceValue, getAllProducts, allRowData])

    return { searchValue, changeSearchValue, listProducts, onSelectProduct, onFocus, ref, handleOnKeyDown, loading }
}

export default ModalUniqueNumberProductOrderSell